<script setup lang="ts">
import { ref, watch } from 'vue';
import { loadCompanyPage, loadFactoryAlbums } from '../company-content';
import type { ContentPage, FactoryAlbum } from '../api';
import { locale, text } from '../i18n';
import { applyDocumentSeo } from '../seo';

defineOptions({ name: 'FactoryView' });

const page = ref<ContentPage | null>(null);
const albums = ref<FactoryAlbum[]>([]);
const error = ref('');

watch(locale, async () => {
  error.value = '';
  try {
    const [nextPage, nextAlbums] = await Promise.all([
      loadCompanyPage(locale.value, 'factory'),
      loadFactoryAlbums(locale.value)
    ]);
    page.value = nextPage;
    albums.value = nextAlbums;
    applyDocumentSeo(nextPage.seo.title, nextPage.seo.description);
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : text('页面加载失败', 'Unable to load page');
  }
}, { immediate: true });
</script>

<template>
  <p v-if="error" class="container portal-page-error">{{ error }}</p>
  <template v-else-if="page">
    <section class="portal-section">
      <div class="container portal-company-page-intro">
        <header class="portal-section-heading portal-section-heading--stacked">
          <span>{{ text('研发制造', 'Engineering and production') }}</span>
          <h2>{{ text('把研发、验证与生产装配放在同一套交付流程中', 'One delivery process across engineering, validation and assembly') }}</h2>
        </header>
        <article v-if="page.contentHtml" class="rich-content portal-company-page-intro__copy" v-html="page.contentHtml" />
      </div>
    </section>

    <section v-if="albums.length" class="portal-section portal-section--soft">
      <div class="container">
        <header class="portal-section-heading">
          <span>{{ text('制造现场', 'Manufacturing environment') }}</span>
          <h2>{{ text('研发与生产环境', 'R&D and production environment') }}</h2>
          <p>{{ text('图集内容由后台独立维护，替换照片不会影响产品、新闻或首页图片。', 'Albums are maintained independently in the content console and do not share display settings with products, news or the homepage.') }}</p>
        </header>
        <section v-for="album in albums" :key="album.id" class="portal-factory-album">
          <h3>{{ album.title }}</h3>
          <div class="portal-factory-gallery" :class="{ 'is-single': album.images.length === 1 }">
            <figure v-for="image in album.images" :key="image.id">
              <img :src="image.url" :alt="image.alt" loading="lazy">
              <figcaption v-if="image.caption">{{ image.caption }}</figcaption>
            </figure>
          </div>
        </section>
      </div>
    </section>
  </template>
</template>
